import { useState } from 'react';
import { Link } from 'react-router-dom';
import { saveFeedback } from './lib/db';

// Build a clean, self-contained HTML guideline summary from the study's education
// modules and trigger a download (the reward for leaving feedback). No backend.
function downloadGuidelines(study) {
  const esc = (s) => String(s).replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
  const body = (study.education || []).map((m) => (
    `<h2>${esc(m.title)}</h2><ul>${(m.points || []).map((p) => `<li>${esc(p)}</li>`).join('')}</ul>` +
    (m.tip ? `<p class="tip"><b>Memory hook —</b> ${esc(m.tip)}</p>` : '')
  )).join('');
  const html = `<!doctype html><html lang="en"><head><meta charset="utf-8">` +
    `<meta name="viewport" content="width=device-width, initial-scale=1">` +
    `<title>${esc(study.educationTitle || study.studyTitle)} — KAPSTAT</title>` +
    `<style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif;max-width:720px;margin:32px auto;padding:0 20px;color:#1c2430;line-height:1.55}` +
    `h1{color:#1f4e79}h2{color:#1f4e79;margin-top:24px}.tip{background:#eef7f5;border-left:4px solid #22E3C3;padding:8px 12px;border-radius:8px}small{color:#6b7686}</style></head><body>` +
    `<h1>${esc(study.educationTitle || study.studyTitle)}</h1>` +
    `<p><small>KAPSTAT guideline summary · ${esc(study.studyTitle)} · for learning, pending expert validation — not a substitute for full clinical guidelines or your institution's protocols.</small></p>` +
    body + `<p><small>kapstat.com</small></p></body></html>`;
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `kapstat_${study.slug}_guidelines.html`;
  a.click();
  URL.revokeObjectURL(a.href);
}

const LEARNED = [['yes', 'Yes'], ['somewhat', 'Somewhat'], ['no', 'Not really']];

// Short, anonymous "how beneficial was this?" prompt shown on the done page. Leaving
// it reveals a downloadable guideline summary. No personal details collected.
export function FeedbackPrompt({ uid, study }) {
  const [useful, setUseful] = useState(0);
  const [learned, setLearned] = useState(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!useful) return;
    setBusy(true);
    try { await saveFeedback(uid, study.slug, { usefulness: useful, learned }); } catch (e) { console.warn('feedback save failed', e); }
    setBusy(false); setDone(true);
  }

  if (done) {
    return (
      <div className="feedback feedback--done">
        <h3>🙏 Thanks — that helps us a lot</h3>
        <p className="muted small">Here are the guidelines to keep:</p>
        <div className="btn-row" style={{ justifyContent: 'center', flexWrap: 'wrap', gap: 10 }}>
          <button className="btn btn--primary" onClick={() => downloadGuidelines(study)}>📥 Download the guidelines</button>
          <Link className="btn btn--ghost" to="/learn">Browse the library</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="feedback">
      <h3>📝 One quick question — then your guidelines</h3>
      <p className="muted small">How useful was this for you? <span className="muted">(anonymous — no details collected)</span></p>
      <div className="rating" role="radiogroup" aria-label="How useful was this for you?">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" role="radio" aria-checked={useful === n} aria-label={`${n} out of 5`}
            className={'rating__star' + (n <= useful ? ' rating__star--on' : '')} onClick={() => setUseful(n)}>★</button>
        ))}
      </div>
      <p className="muted small" style={{ marginTop: 12 }}>Did you learn something new?</p>
      <div className="chip-opts">
        {LEARNED.map(([v, l]) => (
          <button key={v} type="button" className={'chip-opt' + (learned === v ? ' chip-opt--on' : '')}
            aria-pressed={learned === v} onClick={() => setLearned(v)}>{l}</button>
        ))}
      </div>
      <div className="btn-row btn-row--end" style={{ marginTop: 16 }}>
        <button className="btn btn--primary" disabled={!useful || busy} onClick={submit}>{busy ? 'Saving…' : 'Submit & get the guidelines'}</button>
      </div>
      <p className="muted small" style={{ marginTop: 6 }}><Link to="/learn">Not now? Browse the guidelines library →</Link></p>
    </div>
  );
}
